import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import config from '../../app-config'
import {
  get_resources_by_id,
  create_new_resources,
  update_resources,
  delete_resources
} from '../../api/resourcesAPI'
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from '../../layouts/AdminLayout'
import notify from '../../utils/IANotification';
import FileUploadField from '../../components/FileUploadField';
import Switch from "react-switch"
import SubNavbar from '../../components/ui/SubNavbar';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { MdArrowBack, MdSave, MdDelete } from 'react-icons/md';

export default function ResourcesFormPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  useFetchUserData()

  let resource_id = new URLSearchParams(window.location.search).get("resource_id")

  const [activeTab, setActiveTab] = useState("general")
  const [resourceData, setResourceData] = useState({
    name: "",
    is_published: 0,
    available_from: "",
    available_to: "",
    url: "",
  })

  const tabs = [
    { id: 'general', label: 'General Settings' },
    { id: 'availability', label: 'Availability & Dates' },
    { id: 'attachment', label: 'Resource Asset / File' },
  ]

  const fetchResourceById = async () => {
    if (!resource_id || resource_id === "new") return
    const data = await get_resources_by_id(resource_id)
    if (data?.status === 200) {
      setResourceData(data.data)
    } else {
      notify({ type: "error", message: data?.message })
    }
  }

  useEffect(() => {
    fetchResourceById()
  }, [])

  const createResource = async () => {
    const data = await create_new_resources(resourceData)
    if (data?.status === 200) {
      notify({ type: "success", message: "Resource created successfully" })
      navigate("/admin/resources/details?resource_id=" + data.data.id)
    } else {
      notify({ type: "error", message: data?.message })
    }
  }

  const updateResource = async () => {
    if (!resource_id || resource_id === "new") return
    const data = await update_resources(resource_id, resourceData)
    if (data?.status === 200) {
      notify({ type: "success", message: "Resource updated successfully" })
      fetchResourceById()
    } else {
      notify({ type: "error", message: data?.message })
    }
  }

  const deleteResource = async () => {
    if (!resource_id || resource_id === "new") return
    const data = await delete_resources(resource_id)
    if (data?.status === 200) {
      notify({ type: "success", message: "Resource deleted successfully" })
      navigate("/admin/resources")
    } else {
      notify({ type: "error", message: data?.message })
    }
  }

  return (
    <AdminLayout
      navSelected={"Resources"}
      headerText={resource_id === "new" ? "New Resource" : "Edit Resource"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/resources')}>
              Back
            </Button>
            <h1 className='text-xl font-bold text-gray-900'>
              {resource_id === "new" ? "Create Resource" : resourceData.name || "Edit Resource"}
            </h1>
          </div>
          <div className='flex gap-2'>
            {resource_id !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={deleteResource}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (resource_id === "new") createResource()
              else updateResource()
            }}>
              {resource_id === "new" ? "Create" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <SubNavbar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* TAB 1: GENERAL SETTINGS */}
      {activeTab === "general" && (
        <Card title="General Resource Profile" subtitle="Configure resource name and publication status.">
          <div className="space-y-6">
            <div className="input-container">
              <label className="input-label font-semibold">Resource Title / Name</label>
              <input
                type="text"
                className="text-input"
                placeholder='e.g. Methodist Message Issue #45'
                value={resourceData.name || ""}
                onChange={(e) => setResourceData({ ...resourceData, name: e.target.value })}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div>
                <label className="font-semibold text-sm text-gray-900">Publication Status</label>
                <p className="text-xs text-gray-500">Toggle whether this resource is published on the church portal.</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-gray-700">
                  {resourceData.is_published == 1 ? "Published" : "Draft"}
                </span>
                <Switch
                  onChange={(checked) => setResourceData({ ...resourceData, is_published: checked ? 1 : 0 })}
                  checked={resourceData.is_published == 1}
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* TAB 2: AVAILABILITY & DATES */}
      {activeTab === "availability" && (
        <Card title="Access Schedule &amp; Dates" subtitle="Specify when this resource becomes available to members.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="input-container">
              <label className="input-label font-semibold">Available From</label>
              <input
                type="datetime-local"
                className="text-input"
                value={resourceData.available_from || ""}
                onChange={(e) => setResourceData({ ...resourceData, available_from: e.target.value })}
              />
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Available To</label>
              <input
                type="datetime-local"
                className="text-input"
                value={resourceData.available_to || ""}
                onChange={(e) => setResourceData({ ...resourceData, available_to: e.target.value })}
              />
            </div>
          </div>
        </Card>
      )}

      {/* TAB 3: ATTACHMENT */}
      {activeTab === "attachment" && (
        <Card title="Resource Asset File &amp; Links" subtitle="Attach external URLs or upload PDF/media files.">
          {resource_id === "new" ? (
            <p className="text-xs text-gray-400 italic">Please save the resource profile first before uploading file attachments.</p>
          ) : (
            <div className="space-y-6">
              <div className="input-container">
                <label className="input-label font-semibold">Resource External URL</label>
                <input
                  type="url"
                  className="text-input"
                  placeholder="https://..."
                  value={resourceData.url || ""}
                  onChange={(e) => setResourceData({ ...resourceData, url: e.target.value })}
                />
              </div>

              <div className="pt-4 border-t border-gray-100">
                <label className="input-label font-semibold block mb-2">Upload Document File (PDF)</label>
                <FileUploadField
                  upload_url={`${config.endpoint}/file/resources/id/${resource_id}`}
                  onChange={() => fetchResourceById()}
                  max_files={1}
                  text={"Upload PDF / Document File"}
                  subtext={"Drag and drop a file here or click to select"}
                />
              </div>
            </div>
          )}
        </Card>
      )}
    </AdminLayout>
  )
}
